import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, TableCell } from "@mui/material";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { getDefaultMessageResultsFound, getTranslateByKey, haveIgnoreElement } from "@/main/services/utils";
import * as S from "./styles";
import * as StylesShared from "@/main/components/shared/styles";
import { TextEllipsis } from "@/main/components/shared/styles";
import { announcementApi } from "@/main/api";
import { AnnouncementStatusEnum } from "@/main/services/enum/AnnouncementStatusEnum";
import { AnnouncementFilterRequest } from "@/main/models/request/announcementRequest";
import { AnnouncementSearchGridResponse } from "@/main/models/response/announcementResponse";
import RenderTooltipMessage from "@/main/components/shared/renderTooltipMessage/renderTooltipMessage";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import { isNil } from "lodash";
import { AnnouncementCopyModal } from "./announcementCopyModal";

export interface IGridData {
	isLoading: boolean;
	totalItems: number;
	data: AnnouncementSearchGridResponse[];
}

export const INITIAL_GRID_STATE: IGridData = {
	isLoading: false,
	totalItems: 0,
	data: [],
};

interface IAnnouncementSearchGridProps {
	gridData: IGridData;
	setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
	formFilters: any;
}

const AnnouncementSearchGrid: React.FC<IAnnouncementSearchGridProps> = ({ gridData, setGridData, formFilters }) => {
	const navigate = useNavigate();

	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [isOpenModalCopy, setIsOpenModalCopy] = useState(false);
	const [announcementSelected, setAnnouncementSelected] = useState<AnnouncementSearchGridResponse | null>(null);

	function handleRowBoxGrid(guid: string, event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) {
		if (haveIgnoreElement(event.target as HTMLElement)) return;

		navigate(`${RoutesConstants.registrationRegisterAnnouncement}?announcementGuid=${guid}`);
	}

	function onLoadInfiniteScroll(request: AnnouncementFilterRequest) {
		setIsLoadingInfiniteScroll(true);

		const requestGridAnnouncement: AnnouncementFilterRequest = {
			...request,
			pageIndex: request.pageIndex ?? PaginationEnum.PageIndexIntial,
			pageSize: request.pageSize ?? PaginationEnum.PageSize30,
		};

		announcementApi
			.getAnnouncements(requestGridAnnouncement)
			.then((response) => {
				let resultData: any[] = [];
				let resultTotalItems: number = 0;
				if (response.data) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}

				const newData = resultData.filter(
					(item: any) => !gridData.data.some((existing: any) => existing.guid === item.guid),
				);
				const updatedData = [...gridData.data, ...newData];
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: updatedData,
				});
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function onLoadSearchGrid() {
		setPageIndex((prev) => prev + 1);
		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			const updatedFormFilters = {
				...formFiltersOnLoadSearchGrid,
				pageIndex: pageIndex + 1,
			};
			onLoadInfiniteScroll(updatedFormFilters);
		}
	}

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data, gridData.totalItems]);

	useEffect(() => {
		setPageIndex(PaginationEnum.PageIndexIntial);
	}, [formFilters]);

	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);

	const rowHeaders = [
		getTranslateByKey("information.systemApplication"),
		getTranslateByKey("information.company"),
		getTranslateByKey("information.name"),
		getTranslateByKey("information.period"),
		getTranslateByKey("information.whoCanSee"),
		getTranslateByKey("information.creationDate"),
		<StylesShared.RowHeaderTextCenter key="6">
			{getTranslateByKey("information.actions")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="7">
			{getTranslateByKey("information.status")}
		</StylesShared.RowHeaderTextCenter>,
	];

	const sizeColumns = ["10%", "10%", "10%", "15%", "20%", "10%", "10%", "10%"];

	function renderColor(statusRegister: AnnouncementStatusEnum) {
		switch (statusRegister) {
			case AnnouncementStatusEnum.Active:
				return "#00cc66";
			case AnnouncementStatusEnum.Suspended:
				return "#0099ff";
			case AnnouncementStatusEnum.Draft:
				return "#ff9900";
			case AnnouncementStatusEnum.Expired:
				return "#ff0000";
			default:
				return "#fff";
		}
	}

	function handleCopyAnnouncement(event: any, announcement: AnnouncementSearchGridResponse) {
		event.stopPropagation();
		setIsOpenModalCopy(true);
		setAnnouncementSelected(announcement);
	}

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters === null) return null;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	return (
		<>
			<S.Container>
				<BoxGrid
					title={titleGrid}
					sizeColumns={sizeColumns}
					rowHeaders={rowHeaders}
					infiniteScrollConfig={{
						dataLength: gridData.data.length,
						fetchData: () => onLoadSearchGrid(),
						hasMore: hasShowMore,
						isLoading: isLoadingInfiniteScroll,
					}}
				>
					{gridData.data.map((announcement) => (
						<KOGTableRow
							key={announcement.guid}
							id={`id-searchAnnouncement-tableRow-result-${announcement.guid}`}
							onClick={(e) => handleRowBoxGrid(announcement.guid, e)}
						>
							<TableCell>
								<TextEllipsis title={announcement.nameSystemApplication}>
									{announcement.nameSystemApplication}
								</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={announcement.companyName}>{announcement.companyName}</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={announcement.name}>{announcement.name}</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={`${announcement.startDateFormatted} - ${announcement.endDateFormatted}`}>
									{announcement.startDateFormatted} - {announcement.endDateFormatted}
								</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={announcement.whoCanSeeCompanyType.map((x) => x.value).join("; ")}>
									{announcement.whoCanSeeCompanyType.map((x) => x.value).join("; ")}
								</TextEllipsis>
							</TableCell>

							<TableCell>
								<TextEllipsis title={announcement.creationDateFormatted}>
									{announcement.creationDateFormatted}
								</TextEllipsis>
							</TableCell>

							<TableCell align="center">
								<RenderTooltipMessage
									tooltipMessage={getTranslateByKey("information.duplicateConfiguration")}
									placement="top"
								>
									<span>
										<IconButton
											id={`id-searchAnnouncement-duplicate-${announcement.guid}`}
											size="medium"
											color="primary"
											style={{ marginLeft: 5 }}
											onClick={(e) => handleCopyAnnouncement(e, announcement)}
										>
											<FileCopyOutlinedIcon />
										</IconButton>
									</span>
								</RenderTooltipMessage>
							</TableCell>

							<TableCell id={"id-searchAnnouncement-icon-status"}>
								<S.AreaStatus>
									<div
										className="d-flex align-items-center justify-content-center"
										style={{
											backgroundColor: renderColor(announcement?.idAnnouncementStatus),
											height: 30,
											width: 100,
											borderRadius: 5,
											paddingLeft: "20px",
											paddingRight: "20px",
											color: "#fff",
											fontWeight: 500,
										}}
									>
										{announcement?.nameAnnouncementStatus}
									</div>
								</S.AreaStatus>
							</TableCell>
						</KOGTableRow>
					))}
				</BoxGrid>
			</S.Container>

			{!isNil(announcementSelected) && (
				<AnnouncementCopyModal
					announcement={announcementSelected}
					openModal={isOpenModalCopy}
					setOpenModal={(value) => {
						setAnnouncementSelected(null);
						setIsOpenModalCopy(value);
					}}
				/>
			)}
		</>
	);
};

export default AnnouncementSearchGrid;
